import { useCallback, useMemo } from 'react';
import { TundraStorefrontSummary } from '../../../../../common/models';
import { MISSING_REQUIRED_INFORMATION } from '../StoreValidationHelpers';
import {
    FieldValidationResults,
    FormConfig,
    GenericFormReturn,
    SubmitOperationConfig,
    useGenericForm,
} from '../../../common/hooks/useGenericForm';
import { validateExistingStorefrontId, validateThemes } from '../utils/themeValidation';

export interface StoreCreateFormData {
    storeName: string;
    tundraSubdomainPrefix: string;
    existingStorefrontId: string;
    themes: string;
}

/**
 * Custom hook for managing store creation form state and validation.
 * 
 * Handles store name validation, subdomain prefix uniqueness checking,
 * and conditional theme import validation.
 * 
 * @param tundraStorefrontList - List of existing storefronts for uniqueness validation
 * @param submitOperationConfig - Configuration for form submission handling
 * @returns Form state and methods for store creation
 */
export function useStoreCreateForm(
    tundraStorefrontList: TundraStorefrontSummary[] | null,
    submitOperationConfig: SubmitOperationConfig,
): GenericFormReturn<StoreCreateFormData> {

    // Domain validation helper for store ID uniqueness
    const domainValidationCheck = useCallback((storefront: TundraStorefrontSummary, domainToCheck: string): boolean => {
        if (process.env["REACT_APP_ENV"] === "local") {
            // Local development environments tend to run on different ports and may use different domain suffixes
            // depending on developers setup while running self-serve + tundra locally.
            // To support such scenarios, we check if storefrontId includes the domainToCheck
            return storefront.storefrontId.toLowerCase().includes(domainToCheck.toLowerCase()) ||
                storefront.aliases.some(alias =>
                    alias.toLowerCase().includes(domainToCheck.toLowerCase())
                );
        }

        return storefront.storefrontId.toLowerCase() === domainToCheck.toLowerCase() ||
            storefront.aliases.some(alias =>
                alias.toLowerCase() === domainToCheck.toLowerCase()
            );
    }, []);

    // Store ID uniqueness validation
    const isSubdomainPrefixUnique = useCallback((subdomainPrefix: string): boolean => {
        if (!tundraStorefrontList || subdomainPrefix.trim() === "") {
            return false;
        }

        if (process.env["REACT_APP_ENV"] === "local") {
            // Local development might point to DEV or LOCAL Tundra endpoints.
            // When pointing to DEV endpoints, the devPrefix is ".dev".
            // When pointing to LOCAL endpoints, there are variable port values and a ".local" devPrefix.
            const hasLocalDomain = tundraStorefrontList.some(storefront =>
                domainValidationCheck(storefront, `${subdomainPrefix.toLowerCase()}.local`))
            const hasDevDomain = tundraStorefrontList.some(storefront =>
                domainValidationCheck(storefront, `${subdomainPrefix.toLowerCase()}.dev`));
            return !hasLocalDomain && !hasDevDomain;
        }

        let devPrefix = "";
        // On staging environments, we are always point to the DEV tundra endpoint.
        if (process.env["REACT_APP_ENV"] === "staging") {
            devPrefix = ".dev";
        }
        // Else we assume we are in production, where devPrefix = "".
        const domainToCheck = `${subdomainPrefix.toLowerCase()}${devPrefix}.junipercreates.com`;
        return !tundraStorefrontList.some(storefront => domainValidationCheck(storefront, domainToCheck));
    }, [tundraStorefrontList, domainValidationCheck]);

    const initialValues = useMemo(() => ({
        storeName: "",
        tundraSubdomainPrefix: "",
        existingStorefrontId: "",
        themes: "",
    }), []);

    const formConfig: FormConfig<StoreCreateFormData> = useMemo(() => ({
        submitOperationConfig,
        fields: [
            {
                name: "storeName",
                required: true,
                validate: (value: StoreCreateFormData['storeName']) => {
                    if (typeof value !== 'string' || value.trim() === '') {
                        return { isValid: false, statusMessage: 'Store name is required.' };
                    }
                    return { isValid: true };
                },
            },
            {
                name: "tundraSubdomainPrefix",
                required: true,
                validate: (value: StoreCreateFormData['tundraSubdomainPrefix']) => {
                    const isUnique: boolean = isSubdomainPrefixUnique(value);
                    return {
                        isValid: isUnique,
                        statusMessage: isUnique ? "" : "This Store ID is already in use."
                    };
                },
            },
            {
                name: "existingStorefrontId",
                required: false,
                validate: (value: StoreCreateFormData['existingStorefrontId'], formValues: StoreCreateFormData | undefined) => {
                    const themes = formValues?.themes ?? '';
                    return validateExistingStorefrontId(value, themes);
                },
            },
            {
                name: "themes",
                required: false,
                validate: (value: StoreCreateFormData['themes'], formValues: StoreCreateFormData | undefined) => {
                    const existingStorefrontId = formValues?.existingStorefrontId ?? '';
                    return validateThemes(value, existingStorefrontId);
                },
            },
        ],
        validateForm: (formValues: StoreCreateFormData, validationResults: FieldValidationResults<StoreCreateFormData>) => {
            if (validationResults.tundraSubdomainPrefix && !validationResults.tundraSubdomainPrefix.isValid) {
                return {
                    isValid: false,
                    statusMessage: validationResults.tundraSubdomainPrefix.statusMessage ?? "",
                }
            }

            const hasExistingStorefrontId: boolean = !!formValues.existingStorefrontId.trim();
            const hasThemes: boolean = !!formValues.themes?.trim();
            // If an existing storefront ID is provided, then themes are required for the form to be valid.
            const conditionalFieldsValid: boolean = hasExistingStorefrontId ? hasThemes : true;

            return {
                isValid: conditionalFieldsValid,
                statusMessage: conditionalFieldsValid ? "" : MISSING_REQUIRED_INFORMATION,
            };
        },
    }), [isSubdomainPrefixUnique, submitOperationConfig]);

    return useGenericForm<StoreCreateFormData>({
        initialFormStatusMessage: MISSING_REQUIRED_INFORMATION,
        initialValues,
        formConfig,
    });
}
